import {
  GraphQLObjectType,
  GraphQLString,
  GraphQLID,
  GraphQLNonNull,
  GraphQLList,
  GraphQLEnumType,
  GraphQLBoolean,
  GraphQLInt
} from 'graphql'
import { License, license } from './license'
import {
  ApplicationImportanceType,
  ApplicationImportance
} from './application-importance'
import { syslogConfig, SyslogConfig } from './syslog-config'

export interface Application {
  id: string
  applicationUuid: string
  name: string
  language: string
  importance: ApplicationImportance
  path: string
  hasInstrumentationConflict?: boolean
}

export interface ServerSamplingFields {
  profile?: string
  baseline: number
  frequency: number
  window: number
}

interface ServerSampling extends Partial<ServerSamplingFields> {
  enabled: boolean
}

export type StacktraceCaptureMode = 'ALL' | 'SOME' | 'NONE' | 'SINK'
export type LogLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | 'TRACE'

export interface Server {
  id: string
  name: string
  environment: string
  applications: Application[]
  assess: boolean
  tags: string[]
  license: License | null
  online: boolean
  hostname: string
  logLevel: LogLevel
  path: string
  firstActivity: string
  lastActivity: string
  agentVersion: string
  latestAgentVersion?: string
  outOfDate: boolean
  language?: string | null
  syslogConfig: SyslogConfig
  protect: boolean
  botProtection: boolean
  samplingProfile?: string
  sampling: ServerSampling
  stacktraceCaptureMode: StacktraceCaptureMode
  logPath?: string
  isAssessStatusLocked?: boolean
  isProtectStatusLocked?: boolean
  assessEnablePropertyName?: string
  protectEnablePropertyName?: string
  assessEnableLocation?: string
  protectEnableLocation?: string
  diagnosticCollectionEnabled: boolean
}

const serverApplicationType = new GraphQLObjectType({
  name: 'ServerApplication',
  fields: () => ({
    id: {
      description: 'Combination of Application ID and server ID',
      type: new GraphQLNonNull(GraphQLID)
    },
    applicationUuid: {
      description: 'The application ID',
      type: new GraphQLNonNull(GraphQLString)
    },
    name: {
      description: 'Human readable name for the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    language: {
      description: 'Application language',
      type: new GraphQLNonNull(GraphQLString)
    },
    importance: {
      description: 'Application importance',
      type: new GraphQLNonNull(ApplicationImportanceType)
    },
    path: {
      description: 'URL path of the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    hasInstrumentationConflict: {
      description:
        'Whether the application has a Protect instrumentation conflict between Contrast UI and the agent configuration',
      type: GraphQLBoolean
    }
  })
})

export const serverEnvironmentType = new GraphQLEnumType({
  name: 'Environment',
  values: {
    DEVELOPMENT: {
      value: 'DEVELOPMENT'
    },
    QA: {
      value: 'QA'
    },
    PRODUCTION: {
      value: 'PRODUCTION'
    }
  }
})

export const logLevelType = new GraphQLEnumType({
  name: 'LogLevel',
  values: {
    ERROR: {
      value: 'ERROR'
    },
    WARN: {
      value: 'WARN'
    },
    INFO: {
      value: 'INFO'
    },
    DEBUG: {
      value: 'DEBUG'
    },
    TRACE: {
      value: 'TRACE'
    }
  }
})

const serverSamplingType = new GraphQLObjectType({
  name: 'ServerSampling',
  description: '',
  fields: () => ({
    enabled: {
      description: 'Is server sampling enabled?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    baseline: {
      description: 'Sampling baseline',
      type: GraphQLInt
    },
    frequency: {
      description: 'Sampling frequency',
      type: GraphQLInt
    },
    window: {
      description: 'Sampling window',
      type: GraphQLInt
    }
  })
})

export const stacktraceCaptureModeType = new GraphQLEnumType({
  name: 'StacktraceCaptureMode',
  description: '',
  values: {
    ALL: {
      value: 'ALL'
    },
    SOME: {
      value: 'SOME'
    },
    NONE: {
      value: 'NONE'
    },
    SINK: {
      value: 'SINK'
    }
  }
})

export const serverType = new GraphQLObjectType({
  name: 'Server',
  fields: () => ({
    id: {
      description: 'The unique ID for a server',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Human readable name for the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    environment: {
      description: 'Server environment',
      type: new GraphQLNonNull(serverEnvironmentType)
    },
    applications: {
      description: 'Applications on this server',
      type: new GraphQLNonNull(new GraphQLList(serverApplicationType))
    },
    assess: {
      description: 'Assess status for the server',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    tags: {
      description: 'User defined tags',
      type: new GraphQLNonNull(
        new GraphQLList(new GraphQLNonNull(GraphQLString))
      )
    },
    license: {
      description: 'Server license information',
      type: license
    },
    online: {
      description: 'Is the server currently online',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    hostname: {
      description: 'The hostname of the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    path: {
      description: 'The installation path of the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    logLevel: {
      description: 'The log level of the server',
      type: new GraphQLNonNull(logLevelType)
    },
    logPath: {
      description: 'The log path of the server',
      type: GraphQLString
    },
    firstActivity: {
      description: 'Timestamp of the first activity for this server',
      type: new GraphQLNonNull(GraphQLString)
    },
    lastActivity: {
      description: 'Timestamp of the last activity for this server',
      type: new GraphQLNonNull(GraphQLString)
    },
    agentVersion: {
      description: 'The agent version running on the server',
      type: new GraphQLNonNull(GraphQLString)
    },
    latestAgentVersion: {
      description: 'The latest agent version available',
      type: GraphQLString
    },
    outOfDate: {
      description: 'The server agent is out of date',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    language: {
      description: 'The language used by applications on the server',
      type: GraphQLString
    },
    syslogConfig: {
      description: 'The Syslog configuration used by the agent on this server',
      type: new GraphQLNonNull(syslogConfig)
    },
    protect: {
      description: 'Protect status for the server',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    botProtection: {
      description: 'Bot protection status for the server',
      type: GraphQLBoolean
    },
    samplingProfile: {
      description: 'Server sampling profile',
      type: GraphQLString
    },
    sampling: {
      description: 'Server sampling settings',
      type: new GraphQLNonNull(serverSamplingType)
    },
    stacktraceCaptureMode: {
      description: 'Stacktrace capture mode',
      type: stacktraceCaptureModeType
    },
    isAssessStatusLocked: {
      description:
        "Determine if the Asssess 'enabled' status is locked for modification",
      type: GraphQLBoolean
    },
    isProtectStatusLocked: {
      description:
        "Determine if the Protect 'enabled' status is locked for modification",
      type: GraphQLBoolean
    },
    assessEnablePropertyName: {
      description: 'User-provided property name for Assess enabled status',
      type: GraphQLString
    },
    protectEnablePropertyName: {
      description: 'User-provided property name for Protect enabled status',
      type: GraphQLString
    },
    assessEnableLocation: {
      description: 'Human-readable version of the config source for Assess',
      type: GraphQLString
    },
    protectEnableLocation: {
      description: 'Human-readable version of the config source for Protect',
      type: GraphQLString
    },
    diagnosticCollectionEnabled: {
      description:
        'Determine if diagnostic collection is enabled for the server',
      type: GraphQLBoolean
    }
  })
})
export const diagnosticType = new GraphQLObjectType({
  name: 'Diagnostic',
  fields: () => ({
    uuid: {
      description: 'The unique ID for a server',
      type: GraphQLString
    },
    enabled: {
      description: 'Determine if diagnostic collection is enabled',
      type: GraphQLBoolean
    },
    logLevel: {
      description: 'The log level of diagnostic collection',
      type: GraphQLString
    },
    startTime: {
      description: 'Time to begin diagnostic collection',
      type: GraphQLString
    },
    expires: {
      description: 'Time to expire diagnostic collection',
      type: GraphQLString
    }
  })
})
